import { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { BsCopy, BsEnvelopePaper, BsPlusLg, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import CollectionToolbar from '#globalComponents/CollectionView/CollectionToolbar';
import CollectionView from '#globalComponents/CollectionView/CollectionView';
import useCollectionSearch from '#globalComponents/CollectionView/useCollectionSearch';
import useCollectionViewMode from '#globalComponents/CollectionView/useCollectionViewMode';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';

import TemplatePicker from '../../components/templatePicker/TemplatePicker';
import { pdfSheets } from '../../constants/pdfSheets';
import { pmClone } from '../../constants/pmTemplates';
import {
	useCreatePostMailingTemplate,
	useDeletePostMailingTemplate,
	useDuplicatePostMailingTemplate,
	useUpdatePostMailingTemplate,
} from '../../models/postMailingTemplates/mutations';
import { usePostMailingTemplates } from '../../models/postMailingTemplates/queries';

import '../../styles/postmailing.scss';

const LAYOUT_LABEL = {
	simple: 'Schlicht',
	sidebar: 'Mit Seitenleiste',
	coloredSidebar: 'Farbige Seitenleiste',
};

const getSearchText = (template) => template.name || '';

const getTypeLabel = (design) => {
	if (design.kind !== 'pdf') return LAYOUT_LABEL[design.layout || 'simple'] || design.layout;
	if (design.pdf?.apiEligible === false) return 'PDF · Sonderformat';
	const sheetCount = pdfSheets(design).length;
	return sheetCount > 1 ? `PDF · ${sheetCount} Blätter` : 'PDF';
};

const PostMailingTemplatesPage = () => {
	const navigate = useNavigate();
	const { data: templates = [], isLoading } = usePostMailingTemplates();
	const createMutation = useCreatePostMailingTemplate();
	const duplicateMutation = useDuplicatePostMailingTemplate();
	const deleteMutation = useDeletePostMailingTemplate();
	const { mutateAsync: updateTemplate } = useUpdatePostMailingTemplate();

	const [viewMode, setViewMode] = useCollectionViewMode('postMailingTemplates');
	const { search, setSearch, filteredItems: filteredTemplates } = useCollectionSearch(templates, getSearchText);
	const [showPicker, setShowPicker] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);

	const openEditor = useCallback((id) => navigate(`/post-mailing/templates/${id}`), [navigate]);

	const handlePick = useCallback(
		async (tpl) => {
			try {
				const designData = pmClone(tpl);
				const res = await createMutation.mutateAsync({ name: designData.name || 'Neues Mailing', designData });
				const id = res?.template?.id || res?.id;
				if (id) openEditor(id);
			} catch {
				// The global MutationCache shows the toast.
			}
		},
		[createMutation, openEditor],
	);

	const handleDuplicate = useCallback(
		async (tpl) => {
			try {
				const res = await duplicateMutation.mutateAsync(tpl);
				const id = res?.template?.id || res?.id;
				if (id) openEditor(id);
			} catch {
				// The global MutationCache shows the toast.
			}
		},
		[duplicateMutation, openEditor],
	);

	const handleConfirmDelete = async () => {
		try {
			await deleteMutation.mutateAsync(deleteTarget.id);
			setDeleteTarget(null);
		} catch {
			// The global MutationCache shows the toast; keep the dialog open for a retry.
		}
	};

	const items = useMemo(
		() =>
			filteredTemplates.map((tpl) => ({
				id: tpl.id,
				title: tpl.name,
				onOpen: () => openEditor(tpl.id),
				onRename: (name) => updateTemplate({ id: tpl.id, payload: { name } }),
				tags: [{ prefix: 'Typ', label: getTypeLabel(tpl.designData || {}) }],
				date: { label: 'Letzte Änderung', value: tpl.updatedAt ?? tpl.createdAt },
				actions: [
					{ key: 'duplicate', icon: BsCopy, label: 'Duplizieren', onClick: () => handleDuplicate(tpl) },
					{
						key: 'delete',
						icon: BsTrash,
						label: 'Löschen',
						danger: true,
						onClick: () => setDeleteTarget(tpl),
					},
				],
			})),
		[filteredTemplates, openEditor, updateTemplate, handleDuplicate],
	);

	return (
		<Page
			header={
				<PageHeader
					breadcrumbs={[{ label: 'Post-Mailing', to: '/post-mailing' }, 'Vorlagen']}
					title="Postmailing-Vorlagen"
					subtitle="Wiederverwendbare Briefvorlagen für Ihre Post-Mailings"
				/>
			}
		>
			<CollectionToolbar
				search={search}
				onSearchChange={setSearch}
				searchPlaceholder="Vorlage suchen ..."
				viewMode={viewMode}
				onViewModeChange={setViewMode}
			>
				<Button shape="pill" size="sm" onClick={() => setShowPicker(true)}>
					<BsPlusLg className="h-3.5 w-3.5" />
					Neue Vorlage
				</Button>
			</CollectionToolbar>

			<CollectionView
				items={items}
				viewMode={viewMode}
				isLoading={isLoading}
				emptyLabel={search ? `Keine Vorlagen für „${search}“ gefunden.` : undefined}
				createLabel="neue Vorlage erstellen"
				onCreate={() => setShowPicker(true)}
			/>

			<Modal isOpen={showPicker} onClose={() => setShowPicker(false)} size="xl">
				<Panel.Header
					icon={BsEnvelopePaper}
					onClose={() => setShowPicker(false)}
					subtitle="Starten Sie mit einer fertigen Vorlage oder laden Sie Ihr eigenes PDF hoch. Alle Vorlagen halten die PIN-Druckvorgaben für DIN-A4 ein."
				>
					Neue Vorlage starten
				</Panel.Header>
				<Panel.Body>
					{/* The picker's design tokens are CSS variables scoped to .pm-root, and the modal portals out of the page. */}
					<div className="pm-root">
						<TemplatePicker onPick={handlePick} />
					</div>
				</Panel.Body>
			</Modal>

			<ConfirmModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleConfirmDelete}
				title="Vorlage löschen"
				message={`Möchten Sie die Vorlage "${deleteTarget?.name}" wirklich löschen?`}
				isLoading={deleteMutation.isPending}
			/>
		</Page>
	);
};

export default PostMailingTemplatesPage;
